import { deriveMaskedInternationalLinkingPhoneLabel, normalizeLinkingPhonePrefill } from "./linking-phone.js";
import { normalizeLanguageCode } from "./languages.js";

const SHARE_LINK_ENTRY_KEY = "mincool.shareLinkEntry";
const SHARE_LINK_REFERRER_NAME_KEY = "mincool.shareLinkReferrerName";
const QR_LINK_METHOD = "qr";

function storage() {
  if (typeof window === "undefined") {
    return null;
  }
  return window.sessionStorage;
}

function normalizeShareLinkSocialProof(rawProof) {
  if (!rawProof || typeof rawProof !== "object") {
    return null;
  }
  const referrerDisplayName = String(rawProof.referrerDisplayName || "").trim();
  const activeForDays = Math.max(0, Number.parseInt(rawProof.activeForDays, 10) || 0);
  const completedVoiceNotes = Math.max(0, Number.parseInt(rawProof.completedVoiceNotes, 10) || 0);
  const completedDurationSec = Math.max(0, Number.parseInt(rawProof.completedDurationSec, 10) || 0);

  if (!referrerDisplayName && activeForDays <= 0 && completedVoiceNotes <= 0 && completedDurationSec <= 0) {
    return null;
  }

  return {
    referrerDisplayName,
    activeForDays,
    completedVoiceNotes,
    completedDurationSec,
  };
}

function pluralize(value, singular) {
  const normalizedValue = Math.max(0, Number.parseInt(value, 10) || 0);
  return `${normalizedValue} ${singular}${normalizedValue === 1 ? "" : "s"}`;
}

function formatShareLinkTenure(activeForDays) {
  const normalizedDays = Math.max(0, Number.parseInt(activeForDays, 10) || 0);
  if (normalizedDays < 1) {
    return "";
  }
  if (normalizedDays >= 365) {
    return pluralize(Math.max(1, Math.floor(normalizedDays / 365)), "year");
  }
  if (normalizedDays >= 60) {
    return pluralize(Math.max(1, Math.floor(normalizedDays / 30)), "month");
  }
  if (normalizedDays >= 14) {
    return pluralize(Math.max(1, Math.floor(normalizedDays / 7)), "week");
  }
  return pluralize(normalizedDays, "day");
}

function formatShareLinkVoiceNoteActivity({ completedDurationSec = 0, completedVoiceNotes = 0 } = {}) {
  const normalizedDurationSec = Math.max(0, Number.parseInt(completedDurationSec, 10) || 0);
  const normalizedVoiceNotes = Math.max(0, Number.parseInt(completedVoiceNotes, 10) || 0);

  if (normalizedDurationSec >= 7200) {
    return `${pluralize(Math.max(1, Math.floor(normalizedDurationSec / 3600)), "hour")} of voice notes`;
  }
  if (normalizedDurationSec >= 60) {
    return `${pluralize(Math.max(1, Math.floor(normalizedDurationSec / 60)), "minute")} of voice notes`;
  }
  if (normalizedVoiceNotes >= 3) {
    return pluralize(normalizedVoiceNotes, "voice note");
  }
  return "";
}

export function normalizeShareLinkEntry(rawEntry) {
  if (!rawEntry || typeof rawEntry !== "object") {
    return null;
  }

  const prefilledPhoneNumber = normalizeLinkingPhonePrefill(rawEntry.prefilledPhoneNumber || "");
  const recipientDisplayName = String(rawEntry.recipientDisplayName || "").trim();
  const recipientProfileName = String(rawEntry.recipientProfileName || "").trim();
  const chatKind = String(rawEntry.chatKind || "unknown").trim() || "unknown";
  const prefillAllowed = rawEntry.prefillAllowed !== false;
  const prefillBlockedReason = String(rawEntry.prefillBlockedReason || "").trim();
  const socialProof = normalizeShareLinkSocialProof(rawEntry.socialProof);
  const sourceLanguageCode = rawEntry.sourceLanguageCode
    ? normalizeLanguageCode(rawEntry.sourceLanguageCode)
    : "";
  const deliveryLanguageCode = rawEntry.deliveryLanguageCode
    ? normalizeLanguageCode(rawEntry.deliveryLanguageCode)
    : "";
  const uiLanguageHintCode = rawEntry.uiLanguageHintCode
    ? normalizeLanguageCode(rawEntry.uiLanguageHintCode)
    : "";

  if (
    !String(rawEntry.code || "").trim()
    && !prefilledPhoneNumber
    && !recipientDisplayName
    && !recipientProfileName
    && !prefillBlockedReason
    && !socialProof
    && !sourceLanguageCode
    && !deliveryLanguageCode
    && !uiLanguageHintCode
  ) {
    return null;
  }

  return {
    chatKind,
    code: String(rawEntry.code || "").trim(),
    destinationPath: String(rawEntry.destinationPath || "/link").trim() || "/link",
    prefilledPhoneNumber,
    prefillAllowed,
    prefillBlockedReason,
    recipientDisplayName,
    recipientProfileName,
    socialProof,
    sourceLanguageCode,
    deliveryLanguageCode,
    uiLanguageHintCode,
    funnel: rawEntry.funnel || null,
  };
}

export function getShareLinkIdentityLabel(entry) {
  const normalizedEntry = normalizeShareLinkEntry(entry);
  if (!normalizedEntry) {
    return "";
  }
  return normalizedEntry.recipientProfileName || normalizedEntry.recipientDisplayName || "";
}

export function getShareLinkPhoneSuffix(entry) {
  const normalizedEntry = normalizeShareLinkEntry(entry);
  if (!normalizedEntry?.prefilledPhoneNumber) {
    return "";
  }
  const digits = normalizedEntry.prefilledPhoneNumber.replace(/\D/g, "");
  return digits.slice(-4);
}

export function getShareLinkMaskedPhoneLabel(entry) {
  const normalizedEntry = normalizeShareLinkEntry(entry);
  if (!normalizedEntry?.prefilledPhoneNumber) {
    return "";
  }
  return deriveMaskedInternationalLinkingPhoneLabel(normalizedEntry.prefilledPhoneNumber, "za");
}

export function getShareLinkSocialProofLabel(entry) {
  const normalizedEntry = normalizeShareLinkEntry(entry);
  const socialProof = normalizedEntry?.socialProof;
  if (!socialProof) {
    return "";
  }

  const tenure = formatShareLinkTenure(socialProof.activeForDays);
  const activity = formatShareLinkVoiceNoteActivity(socialProof);
  if (!tenure || !activity) {
    return "";
  }

  const referrerDisplayName = socialProof.referrerDisplayName || "Someone you know";
  return `${referrerDisplayName} has used Min for ${tenure} and already used it on ${activity}.`;
}

export function getStoredShareLinkReferrerName() {
  const store = storage();
  if (!store) {
    return "";
  }
  return String(store.getItem(SHARE_LINK_REFERRER_NAME_KEY) || "").trim();
}

export function clearStoredShareLinkReferrerName() {
  const store = storage();
  if (!store) {
    return;
  }
  store.removeItem(SHARE_LINK_REFERRER_NAME_KEY);
}

export function getPreferredLinkingMethod(search = "") {
  const params = new URLSearchParams(search);
  const preferredMethod = String(params.get("linkMethod") || "").trim().toLowerCase();
  return preferredMethod === QR_LINK_METHOD ? QR_LINK_METHOD : "";
}

export function buildQrPreferredLinkUrl(value) {
  const rawValue = String(value || "").trim();
  if (!rawValue) {
    return "";
  }

  let url;
  try {
    url = new URL(rawValue);
  } catch {
    if (typeof window === "undefined") {
      return rawValue;
    }
    url = new URL(rawValue, window.location.origin);
  }

  url.searchParams.set("linkMethod", QR_LINK_METHOD);
  return url.toString();
}

export function shouldCaptureShareLinkForLinkPage({
  urlCode = "",
  attributionCode = "",
  shareLinkEntry = null,
} = {}) {
  const normalizedUrlCode = String(urlCode || "").trim();
  if (!normalizedUrlCode) {
    return false;
  }

  const normalizedAttributionCode = String(attributionCode || "").trim();
  const normalizedShareLinkEntry = normalizeShareLinkEntry(shareLinkEntry);
  const normalizedShareLinkCode = String(normalizedShareLinkEntry?.code || "").trim();

  return normalizedAttributionCode !== normalizedUrlCode || normalizedShareLinkCode !== normalizedUrlCode;
}

export function shouldPrimeLinkSessionForShareLink(entry) {
  const normalizedEntry = normalizeShareLinkEntry(entry);
  if (!normalizedEntry) {
    return false;
  }

  const destinationPath = String(normalizedEntry.destinationPath || "/link").trim() || "/link";
  return destinationPath.startsWith("/link")
    && normalizedEntry.prefillBlockedReason !== "already_linked";
}

export function shouldOfferShareLinkConfirmation(entry) {
  const normalizedEntry = normalizeShareLinkEntry(entry);
  return Boolean(
    normalizedEntry
    && normalizedEntry.chatKind === "direct"
    && (
      (normalizedEntry.prefillAllowed && normalizedEntry.prefilledPhoneNumber)
      || normalizedEntry.prefillBlockedReason === "already_linked"
    )
  );
}

export function rememberShareLinkEntry(entry) {
  const store = storage();
  if (!store) {
    return;
  }

  const normalizedEntry = normalizeShareLinkEntry(entry);
  if (!normalizedEntry) {
    store.removeItem(SHARE_LINK_ENTRY_KEY);
    store.removeItem(SHARE_LINK_REFERRER_NAME_KEY);
    return;
  }

  store.setItem(SHARE_LINK_ENTRY_KEY, JSON.stringify(normalizedEntry));
  const referrerDisplayName = String(normalizedEntry.socialProof?.referrerDisplayName || "").trim();
  if (referrerDisplayName) {
    store.setItem(SHARE_LINK_REFERRER_NAME_KEY, referrerDisplayName);
  } else {
    store.removeItem(SHARE_LINK_REFERRER_NAME_KEY);
  }
}

export function getShareLinkEntry() {
  const store = storage();
  if (!store) {
    return null;
  }

  const rawValue = store.getItem(SHARE_LINK_ENTRY_KEY);
  if (!rawValue) {
    return null;
  }

  try {
    return normalizeShareLinkEntry(JSON.parse(rawValue));
  } catch {
    store.removeItem(SHARE_LINK_ENTRY_KEY);
    return null;
  }
}

export function clearShareLinkEntry({ preserveReferrerName = false } = {}) {
  const store = storage();
  if (!store) {
    return;
  }
  store.removeItem(SHARE_LINK_ENTRY_KEY);
  if (!preserveReferrerName) {
    store.removeItem(SHARE_LINK_REFERRER_NAME_KEY);
  }
}
